import i18n from '@/plugins/i18n';
import { DEFAULT_LOCALE } from '@/constants/locale-constants';
import { PUBLIC_MESSAGES } from '@common/constants/messages';

/**
 * Mensagens de erro do backend no idioma da interface.
 *
 * O backend responde sempre em português: { mensagem: '<texto>. (Ref.: ERR-XX-000 · abc123)', errorCode }.
 * Em pt-BR nada muda. Em outro idioma, a mensagem é trocada pela tradução do errorCode
 * (backendErrors.<código>), reaproveitando os valores do meio do texto ({provedor}) e mantendo
 * a referência para o suporte. Mensagem sem código (ou código sem tradução) não é traduzível:
 * fica marcada para a tela usar o próprio texto dela (ver resolveErrorMessage).
 */

const REFERENCE = /\.?\s*\(Ref\.:\s*([^)]*)\)\s*$/;
const PLACEHOLDER = /\{(\w+)\}/g;

/** Marca na resposta: a mensagem do backend já está no idioma da interface. */
export const LOCALIZED_FLAG = '__localizedMessage';

export function backendMessageKey(errorCode) {
  return `backendErrors.${String(errorCode).replace(/-/g, '_')}`;
}

const escapeRegex = (text) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/* Valores que o backend pôs no lugar dos {placeholders} do texto do catálogo. */
function _extractParams(template, text) {
  const names = [...template.matchAll(PLACEHOLDER)].map((m) => m[1]);

  if (!names.length) return {};

  const pattern = escapeRegex(template.replace(/\.$/, '')).replace(/\\\{\w+\\\}/g, '(.+?)');
  const match = text.replace(/\.$/, '').match(new RegExp(`^${pattern}$`));

  return match ? Object.fromEntries(names.map((name, i) => [name, match[i + 1]])) : {};
}

/**
 * @returns {string|null} a mensagem traduzida, ou null quando não há tradução para ela
 */
export function translateBackendMessage(message, errorCode, locale = i18n.locale) {
  if (typeof message !== 'string' || !errorCode) return null;

  const key = backendMessageKey(errorCode);

  if (!i18n.te(key, locale)) return null;

  const reference = (message.match(REFERENCE) || [])[1];
  const body = message.replace(REFERENCE, '').trim();
  const template = PUBLIC_MESSAGES[errorCode] || '';
  const text = i18n.t(key, locale, _extractParams(template, body));

  return reference ? `${text.replace(/\.$/, '')}. (Ref.: ${reference})` : text;
}

const MESSAGE_FIELDS = ['mensagem', 'message'];

/** Texto original do backend (português), guardado quando a mensagem é traduzida. */
export const ORIGINAL_FIELD = '__originalMessage';

/**
 * Traduz a mensagem da resposta de erro no lugar, uma vez só.
 * - pt-BR: só marca como pronta (o texto do backend é o padrão).
 * - outro idioma com tradução do errorCode: troca mensagem/message pela tradução.
 * - outro idioma sem tradução: deixa o texto como veio e marca como não traduzido;
 *   resolveErrorMessage usa então o texto da tela (já traduzido) com a referência.
 */
export function localizeErrorResponse(data, locale = i18n.locale) {
  if (!data || typeof data !== 'object' || data[LOCALIZED_FLAG] !== undefined) return data;

  if (locale === DEFAULT_LOCALE) {
    data[LOCALIZED_FLAG] = true;

    return data;
  }

  const field = MESSAGE_FIELDS.find((name) => typeof data[name] === 'string' && data[name].trim());
  const translated = field ? translateBackendMessage(data[field], data.errorCode, locale) : null;

  if (translated) {
    data[ORIGINAL_FIELD] = data[field];

    MESSAGE_FIELDS.forEach((name) => {
      if (typeof data[name] === 'string') data[name] = translated;
    });
  }

  data[LOCALIZED_FLAG] = !!translated;

  return data;
}

/** Texto do backend como ele mandou (para comparação por texto, que não pode depender do idioma). */
export function originalBackendMessage(data) {
  return (data && (data[ORIGINAL_FIELD] || data.mensagem || data.message)) || '';
}

/** A mensagem do backend pode ir para a tela neste idioma? (pt-BR sempre; outro, só se traduzida). */
export function isBackendMessageShowable(data) {
  if (i18n.locale === DEFAULT_LOCALE) return true;

  return !!(data && data[LOCALIZED_FLAG]);
}
